/**
 * useNoteSigning Hook
 *
 * Manages note signing workflow including:
 * - Sign note (with electronic signature for note_signing users)
 * - Complete note (without signature for users without note_signing)
 * - Amend completed note
 * - Signing onboarding flow
 *
 * Handles all signing-related mutations, analytics, and user feedback.
 */

import { useCallback, useState } from 'react';
import { useUpdateNoteMutation } from '@features/notes';
import { useUser } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

interface UseNoteSigningOptions {
  noteId: string;
  encounterId?: string;
  hasPatient?: boolean;
  hasNoteSigning?: boolean;
  isSigned?: boolean;
  needsCosign?: boolean;
  onNoteFinalized?: () => void | Promise<void>;
}

interface UseNoteSigningReturn {
  /** Whether the signing onboarding dialog is open */
  isOnboardingOpen: boolean;
  /** Set onboarding dialog open state */
  setIsOnboardingOpen: (open: boolean) => void;
  /** Whether a note update is in progress */
  isUpdatingNote: boolean;
  /** Handle Sign Note button click - signs with signature or completes without */
  handleSignNote: () => Promise<void>;
  /** Handle Amend Note from completed mode */
  handleAmendNote: () => Promise<void>;
  /** Handle onboarding completion - signs note after onboarding */
  handleOnboardingComplete: () => Promise<void>;
  /** Handle resubmit for co-sign (from edits_requested → ready) */
  handleResubmit: () => Promise<void>;
}

export function useNoteSigning({
  noteId,
  encounterId,
  hasPatient = false,
  hasNoteSigning = false,
  isSigned = false,
  needsCosign = false,
  onNoteFinalized,
}: UseNoteSigningOptions): UseNoteSigningReturn {
  const { enqueueSnackbar } = useSnackbar();
  const { note: noteMetrics } = useEventTracking();
  const { userState } = useUser();

  // Local state for note signing onboarding dialog
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);

  // RTK Query mutation
  const [updateNote, { isLoading: isUpdatingNote }] = useUpdateNoteMutation();

  const notifyNoteFinalized = useCallback(() => {
    if (!onNoteFinalized) return;
    void Promise.resolve(onNoteFinalized()).catch(error => {
      console.error('Failed to run note finalization callback:', error);
    });
  }, [onNoteFinalized]);

  /**
   * Perform note signing operation (with electronic signature)
   * Used for users with note_signing feature flag and a patient
   *
   * NOTE: Only updates completion_status. Backend should handle review_status
   * based on completion_status change (e.g., set to 'archived' when completed).
   */
  const performNoteSign = useCallback(async () => {
    if (!noteId) return;

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'completed',
        },
      }).unwrap();

      notifyNoteFinalized();

      // Show success message
      enqueueSnackbar(
        needsCosign ? 'Note submitted for co-signature' : 'Note signed successfully!',
        { variant: 'success' },
      );

      // Track analytics
      noteMetrics.sign(noteId, {
        has_patient: hasPatient,
        source: 'note-top-bar-edit',
      });
    } catch (error) {
      console.error('Failed to sign note:', error);
      enqueueSnackbar('Failed to sign note. Please try again.', { variant: 'error' });

      // Track completion failed
      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        action_type: 'sign',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, notifyNoteFinalized, enqueueSnackbar, noteMetrics, hasPatient, encounterId, needsCosign]);

  /**
   * Perform note completion (without electronic signature)
   * Used for users without note_signing feature or without a patient
   *
   * NOTE: Only updates completion_status. Backend should handle review_status
   * based on completion_status change (e.g., set to 'archived' when completed).
   */
  const performNoteComplete = useCallback(async () => {
    if (!noteId) return;

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'completed',
        },
      }).unwrap();

      notifyNoteFinalized();

      if (needsCosign) {
        enqueueSnackbar('Note submitted for co-signature', { variant: 'success' });
      }

      // Note completed successfully - no additional tracking needed
      // (completion without signing is a valid outcome)
    } catch (error) {
      console.error('Failed to complete note:', error);
      enqueueSnackbar('Failed to complete note. Please try again.', { variant: 'error' });

      // Track completion failed
      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        action_type: 'complete',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, notifyNoteFinalized, enqueueSnackbar, noteMetrics, encounterId, needsCosign]);

  /**
   * Handle Sign Note button click
   * - For users with note_signing + patient: signs with electronic signature (shows onboarding if needed)
   * - For other users: completes without signature
   */
  const handleSignNote = useCallback(async () => {
    if (!noteId) return;

    // Track sign/complete click
    noteMetrics.reviewSignClicked?.(noteId, {
      has_patient: hasPatient,
      has_note_signing: hasNoteSigning,
      encounter_id: encounterId,
    });

    if (hasNoteSigning && hasPatient) {
      // Users with signing capability - check if onboarding is needed first
      if (
        userState?.featureFlags?.note_signing &&
        !userState?.onboardingSteps?.note_signing_onboarding_completed
      ) {
        setIsOnboardingOpen(true);
        return; // Show onboarding instead of signing
      }

      // Perform the signing operation
      await performNoteSign();
    } else {
      // Users without signing capability - complete directly
      // Track Complete click
      noteMetrics.completeClicked?.(noteId, {
        has_patient: hasPatient,
        source: 'note-top-bar-edit',
        encounter_id: encounterId,
      });

      await performNoteComplete();
    }
  }, [
    noteId,
    hasPatient,
    hasNoteSigning,
    encounterId,
    noteMetrics,
    userState,
    performNoteSign,
    performNoteComplete,
  ]);

  /**
   * Handle Amend Note from completed mode
   * Reopens a completed note for editing
   */
  const handleAmendNote = useCallback(async () => {
    if (!noteId) return;

    // Track Amend click
    noteMetrics.amendClicked?.(noteId, {
      was_signed: isSigned,
      encounter_id: encounterId,
    });

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'reviewing',
        },
      }).unwrap();

      // Note amended successfully - status changed back to reviewing
    } catch (error) {
      console.error('Failed to amend note:', error);
      enqueueSnackbar('Failed to reopen note. Please try again.', { variant: 'error' });

      // Track completion failed
      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        action_type: 'amend',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, enqueueSnackbar, noteMetrics, isSigned, encounterId]);

  /**
   * Handle onboarding completion
   * Called when user finishes the signing onboarding flow
   * Automatically signs the note after onboarding completes
   */
  const handleOnboardingComplete = useCallback(async () => {
    // Close the onboarding dialog
    setIsOnboardingOpen(false);

    // Perform the signing operation
    await performNoteSign();
  }, [performNoteSign]);

  /**
   * Handle resubmit for co-sign (from edits_requested → completed)
   * Used when author has addressed supervisor feedback and is resubmitting.
   * Backend routes to "ready" internally based on requires_cosigning.
   */
  const handleResubmit = useCallback(async () => {
    if (!noteId) return;

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'completed',
        },
      }).unwrap();

      enqueueSnackbar('Note resubmitted for co-signature', { variant: 'success' });
    } catch (error) {
      console.error('Failed to resubmit note:', error);
      enqueueSnackbar('Failed to resubmit note. Please try again.', { variant: 'error' });

      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        action_type: 'complete',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, enqueueSnackbar, noteMetrics, encounterId]);

  return {
    isOnboardingOpen,
    setIsOnboardingOpen,
    isUpdatingNote,
    handleSignNote,
    handleAmendNote,
    handleOnboardingComplete,
    handleResubmit,
  };
}
